import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { ModeContext } from "../../App";

const FUEL_TYPES = ["petrol", "diesel", "electric", "hybrid", "cng", "lpg", "other"];
const TRANSMISSION_TYPES = ["manual", "automatic", "cvt", "semi_automatic", "hybrid", "other"];

const AddVehicleLayer = () => {
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [transmissionType, setTransmissionType] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const token = localStorage.getItem("token");
  const { mode } = useContext(ModeContext);
  const navigate = useNavigate();

  // Fetch car owners (role=user) to assign the new vehicle to
  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/users?limit=200&role=user`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await response.json();
        if (response.ok) {
          const options = (json.data.users || []).map((user) => ({
            value: user.id,
            label: `${user.fullName} (${user.username})`,
          }));
          setOwnerOptions(options);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchOwners();
  }, [token]);

  const selectColorStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: mode !== "light" ? "#273142" : "#fff",
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 1px #0d6efd" : "none",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: mode !== "light" ? "#273142" : "#fff",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#24A97B"
        : state.isFocused
        ? (mode !== "light" ? "#1f2836" : "#f0f0f0")
        : "transparent",
      color: mode !== "light" ? "#fff" : "#000",
    }),
    singleValue: (base) => ({
      ...base,
      color: mode !== "light" ? "#fff" : "#000",
    }),
    input: (base) => ({
      ...base,
      color: mode !== "light" ? "#fff" : "#000",
    }),
    placeholder: (base) => ({
      ...base,
      color: mode !== "light" ? "#cfcfcf" : "#6c757d",
    }),
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      setImage(file);
    }
  };

  const validateForm = () => {
    let errors = {};
    if (!selectedOwner) errors.owner = "Owner is required";
    if (!brand) errors.brand = "Brand is required";
    if (!model) errors.model = "Model is required";
    if (!year) errors.year = "Year is required";
    if (!licensePlate) errors.licensePlate = "License plate is required";
    if (!fuelType) errors.fuelType = "Fuel type is required";
    if (!transmissionType) errors.transmissionType = "Transmission type is required";

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => toast.error(error));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("brand", brand);
    formData.append("model", model);
    formData.append("year", year);
    formData.append("licensePlate", licensePlate);
    formData.append("fuelType", fuelType);
    formData.append("transmissionType", transmissionType);
    if (image) {
      formData.append("image", image);
    }

    const loading = toast.loading("Creating vehicle...");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/vehicles?user_id=${selectedOwner.value}`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await response.json();

      if (response.ok) {
        toast.dismiss(loading);
        toast.success("Vehicle created successfully");
        navigate("/all-vehicles");
      } else {
        toast.dismiss(loading);
        toast.error(json.errorMessage || "Failed to create vehicle");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while creating the vehicle");
    } finally {
      toast.dismiss(loading);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-40">
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Vehicle Image */}
            <div className="col-12 mb-24">
              <h6 className="text-md text-primary-light mb-16">Vehicle Image</h6>
              <div className="mb-24 mt-16">
                <div className="avatar-upload">
                  <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      hidden
                      onChange={handleImageChange}
                    />
                    <label
                      htmlFor="imageUpload"
                      className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle"
                    >
                      <Icon icon="solar:camera-outline" className="icon"></Icon>
                    </label>
                  </div>
                  <div className="avatar-preview">
                    <div
                      id="imagePreview"
                      style={{
                        backgroundImage: imagePreview ? `url(${imagePreview})` : "none",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Owner <span className="text-danger-600">*</span>
                </label>
                <Select
                  value={selectedOwner}
                  onChange={setSelectedOwner}
                  options={ownerOptions}
                  placeholder="Select the vehicle owner..."
                  styles={mode !== "light" ? selectColorStyles : undefined}
                  classNamePrefix="select2-selection"
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Brand <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="e.g. Toyota"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Model <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="e.g. Corolla"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Year <span className="text-danger-600">*</span>
                </label>
                <input
                  type="number"
                  className="form-control radius-8"
                  placeholder="e.g. 2022"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  License Plate <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="Enter license plate"
                  value={licensePlate}
                  onChange={(e) => setLicensePlate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Fuel Type <span className="text-danger-600">*</span>
                </label>
                <select
                  className="form-select radius-8"
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  required
                >
                  <option value="">Select fuel type</option>
                  {FUEL_TYPES.map((type) => (
                    <option key={type} value={type} className="text-capitalize">
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Transmission Type <span className="text-danger-600">*</span>
                </label>
                <select
                  className="form-select radius-8"
                  value={transmissionType}
                  onChange={(e) => setTransmissionType(e.target.value)}
                  required
                >
                  <option value="">Select transmission type</option>
                  {TRANSMISSION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
              <button
                type="button"
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                onClick={() => navigate("/all-vehicles")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVehicleLayer;
